import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import type { Feature } from "@/gen/omnios/v1/admin_pb.js";
import { PluginService } from "@/gen/omnios/v1/plugin_connect.js";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Link2, Loader2, Puzzle, Sparkles, Unlink } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/features/$id")({
	component: FeatureDetailPage,
});

const adminClient = createClient(AdminService, transport);
const pluginClient = createClient(PluginService, transport);

function FeatureDetailPage() {
	const { t } = useTranslation("admin");
	const { id } = Route.useParams();
	const queryClient = useQueryClient();
	const [linkOpen, setLinkOpen] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "features"],
		queryFn: () => adminClient.listFeatures({}),
	});

	const feature = data?.features.find((f) => f.id === id);
	useBreadcrumbs([
		{ label: t("admin_root"), href: "/admin" },
		{ label: t("features_title"), href: "/admin/features" },
		{ label: feature?.name ?? "..." },
	]);

	if (isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-8 w-48" />
				<Skeleton className="h-40" />
				<Skeleton className="h-64" />
			</div>
		);
	}

	if (!feature) {
		return (
			<div className="text-center py-12 text-muted-foreground">
				{t("feature_not_found")}
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Info card */}
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-base">
						{t("feature_description")}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					{feature.description ? (
						<p className="text-sm text-muted-foreground">
							{feature.description}
						</p>
					) : (
						<p className="text-sm text-muted-foreground italic">
							{t("feature_no_description")}
						</p>
					)}
					{feature.impact && (
						<div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-2">
							<span className="font-medium">{t("feature_when_disabled")}</span>{" "}
							{feature.impact}
						</div>
					)}
				</CardContent>
			</Card>

			{/* Plugins */}
			<Card>
				<CardHeader className="pb-3">
					<div className="flex items-center justify-between">
						<CardTitle className="text-base">
							{t("feature_linked_plugins")}
						</CardTitle>
						<Dialog open={linkOpen} onOpenChange={setLinkOpen}>
							<DialogTrigger asChild>
								<Button variant="outline" size="sm">
									<Link2 className="h-3.5 w-3.5 mr-1.5" />
									{t("feature_link_plugin")}
								</Button>
							</DialogTrigger>
							<DialogContent>
								<LinkPluginForm
									feature={feature}
									onSuccess={() => {
										setLinkOpen(false);
										queryClient.invalidateQueries({
											queryKey: ["admin", "features"],
										});
									}}
								/>
							</DialogContent>
						</Dialog>
					</div>
					<CardDescription>{t("feature_plugins_description")}</CardDescription>
				</CardHeader>
				<CardContent>
					{feature.plugins.length === 0 ? (
						<div className="text-center py-6 text-sm text-muted-foreground">
							<Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
							{t("feature_no_plugins")}
						</div>
					) : (
						<div className="space-y-2">
							{feature.plugins.map((plugin) => (
								<PluginRow
									key={plugin.pluginId}
									plugin={plugin}
									featureSlug={feature.slug}
								/>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

function PluginRow({
	plugin,
	featureSlug,
}: {
	plugin: Feature["plugins"][number];
	featureSlug: string;
}) {
	const { t } = useTranslation("admin");
	const queryClient = useQueryClient();
	const [unlinking, setUnlinking] = useState(false);

	const handleUnlink = async () => {
		setUnlinking(true);
		try {
			await adminClient.unlinkPluginFromFeature({
				featureSlug,
				pluginId: plugin.pluginId,
			});
			queryClient.invalidateQueries({
				queryKey: ["admin", "features"],
			});
		} finally {
			setUnlinking(false);
		}
	};

	return (
		<div className="flex items-center justify-between bg-muted/30 rounded px-3 py-2.5">
			<div className="flex items-center gap-2">
				<Puzzle className="h-4 w-4 text-muted-foreground" />
				<span className="text-sm font-medium">{plugin.pluginName}</span>
				<code className="text-xs text-muted-foreground">
					{plugin.pluginSlug}
				</code>
				{plugin.isRequired && (
					<Badge variant="secondary" className="text-[10px] px-1.5 py-0">
						{t("feature_plugin_required")}
					</Badge>
				)}
				{plugin.isInstalled && (
					<Badge
						variant="outline"
						className="text-[10px] px-1.5 py-0 text-green-600 border-green-300"
					>
						{t("feature_plugin_installed")}
					</Badge>
				)}
			</div>
			<Button
				variant="ghost"
				size="sm"
				className="h-7 w-7 p-0"
				onClick={handleUnlink}
				disabled={unlinking}
				title={t("feature_plugin_unlink")}
			>
				{unlinking ? (
					<Loader2 className="h-3.5 w-3.5 animate-spin" />
				) : (
					<Unlink className="h-3.5 w-3.5" />
				)}
			</Button>
		</div>
	);
}

function LinkPluginForm({
	feature,
	onSuccess,
}: {
	feature: Feature;
	onSuccess: () => void;
}) {
	const { t } = useTranslation("admin");
	const [pluginId, setPluginId] = useState("");
	const [isRequired, setIsRequired] = useState(true);

	const { data: pluginsData } = useQuery({
		queryKey: ["plugins", "all"],
		queryFn: () => pluginClient.listPlugins({}),
	});

	const availablePlugins = (pluginsData?.plugins ?? []).filter(
		(p) => !feature.plugins.some((fp) => fp.pluginId === p.id),
	);

	const mutation = useMutation({
		mutationFn: () =>
			adminClient.linkPluginToFeature({
				featureSlug: feature.slug,
				pluginId,
				isRequired,
			}),
		onSuccess,
	});

	return (
		<>
			<DialogHeader>
				<DialogTitle>{t("feature_link_title")}</DialogTitle>
				<DialogDescription>
					{t("feature_link_description", { name: feature.name })}
				</DialogDescription>
			</DialogHeader>
			<div className="space-y-4 py-4">
				<div className="space-y-2">
					<Label>{t("feature_link_select_plugin")}</Label>
					<Select value={pluginId} onValueChange={setPluginId}>
						<SelectTrigger>
							<SelectValue placeholder={t("feature_link_select_placeholder")} />
						</SelectTrigger>
						<SelectContent>
							{availablePlugins.map((plugin) => (
								<SelectItem key={plugin.id} value={plugin.id}>
									{plugin.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					{availablePlugins.length === 0 && (
						<p className="text-xs text-muted-foreground">
							{t("feature_link_no_available")}
						</p>
					)}
				</div>
				<div className="flex items-center justify-between">
					<div className="space-y-0.5">
						<Label>{t("feature_link_required")}</Label>
						<p className="text-xs text-muted-foreground">
							{t("feature_link_required_hint")}
						</p>
					</div>
					<Switch checked={isRequired} onCheckedChange={setIsRequired} />
				</div>
			</div>
			<DialogFooter>
				<Button
					onClick={() => mutation.mutate()}
					disabled={mutation.isPending || !pluginId}
				>
					{mutation.isPending ? (
						<>
							<Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
							{t("feature_linking")}
						</>
					) : (
						t("feature_link_submit")
					)}
				</Button>
			</DialogFooter>
		</>
	);
}
